/**
 * Pure assembly of the injectable Cohesive block-token stylesheet.
 *
 * Split out of cohesiveThemeCss.ts / cohesiveBlockTokens.ts so it has NO
 * Vite-specific imports (`?raw`): the same function serves both the runtime
 * modules (which feed it `?raw`-imported stylesheet text) and
 * `scripts/build-block-tokens-css.ts` (a plain-node `tsx` script that feeds
 * it `fs`-read text to emit the hosted `block-tokens.v1.css`). One assembly
 * path means the hosted file cannot drift from what the app injects — locked
 * by the drift test in blockTokensAssemble.test.ts.
 */

/**
 * Reduce a design-system stylesheet to what a browser can use inside an
 * iframe `srcDoc` / a standalone stylesheet. A line/block filter, NOT a CSS
 * transformer:
 * - Comments first, so commented-out imports/blocks don't survive.
 * - `@theme` / `@theme inline` blocks are Tailwind v4 build-time constructs
 *   (declarations only — no nested braces; the drift test asserts no residue).
 * - `@import` lines are dropped — resolved by explicit concatenation instead.
 * - `@layer base` is KEPT: unlayered agent CSS beats layered tokens.
 */
export function stripForIframeInjection(css: string): string {
  return css
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/@theme[^{]*\{[^{}]*\}/g, "")
    .replace(/^\s*@import[^\n]*$/gm, "")
    .replace(/\n\s*\n+/g, "\n")
    .trim();
}

/** The block webfonts as `@import` rules, for contexts that carry tokens as a
 *  lone stylesheet (the detail pane's `customCss` seam, the hosted
 *  block-tokens.v1.css) and therefore can't also add <link> elements. */
export const COHESIVE_BLOCK_FONT_IMPORTS = `@import url("https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Source+Serif+4:ital,opsz,wght@0,8..60,200..900;1,8..60,200..900&display=swap");
`;

/** Font stacks for agent-authored blocks — defined here (not in the design
 *  system CSS) because they are iframe-specific: the webfont links/imports are
 *  what make them resolve inside a sandboxed document. Unlayered on purpose:
 *  it follows the theme CSS in the same stylesheet, and agent CSS (later in
 *  <head>) still wins ties. */
export const BLOCK_FONT_TOKENS_CSS = `
:root {
  --font-sans: "DM Sans", sans-serif;
  --font-serif: "Source Serif 4", serif;
  --font-mono: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}`;

export interface AssembleBlockTokensCssOptions {
  /** Prefix the webfont `@import` rules, for callers that inject this CSS as
   *  a lone stylesheet and therefore can't also add <link> elements. */
  fontImports?: boolean;
}

/**
 * Assemble the full block-token stylesheet from the two design-system source
 * files (`cohesive-tokens.css`, `cohesive-theme.css`, in their `@import`
 * order). Ships BOTH light and dark values — the stylesheet's own `.dark`
 * class selector picks the mode.
 */
export function assembleBlockTokensCss(
  cohesiveTokensCssRaw: string,
  cohesiveThemeCssRaw: string,
  options: AssembleBlockTokensCssOptions = {},
): string {
  const tokens =
    [
      stripForIframeInjection(cohesiveTokensCssRaw),
      stripForIframeInjection(cohesiveThemeCssRaw),
    ].join("\n") + BLOCK_FONT_TOKENS_CSS;
  return options.fontImports ? COHESIVE_BLOCK_FONT_IMPORTS + tokens : tokens;
}

/** Banner for the generated hosted stylesheet (shared by the generator script
 *  and the drift test). `/*!` keeps it survivable through minifiers. */
export const BLOCK_TOKENS_CSS_BANNER = `/*! Cohesive block tokens v1 — GENERATED, do not edit.
 * Source: packages/ui-components/src/styles/{cohesive-tokens,cohesive-theme}.css
 * Regenerate: yarn workspace @cohesive/ui-components build:block-tokens */
`;
